import React, {useEffect, useState} from "react";
import {hot} from "react-hot-loader";
import PropTypes from "prop-types";
import {urlParams} from "../utils/request";
import css from "./App.scss";
import RequestHelpHeader from "./RequestHelpHeader";
import RequestHelpForm from "./RequestHelpForm";
import useStarfishAPI from 'hooks/useStarfishAPI';
import Footer from 'common/Footer';
import StyledButton from 'common/StyledButton';
import StickyFooterLayout from 'common/StickyFooterLayout';
import Toast from 'common/Toast';
import HelpResources from 'helpPage/HelpResources';
import RequestHelpError from './RequestHelpError';

const App = ({isStudent, studentId, closeModal, cancelButtonText, successRequestHelp, studentSelfFlaggingEnabled, requestHelpEnabled}) => {
  const [toastMessage, setToastMessage] = useState("");
  const [isFormInvalid, setFormInvalid] = useState(true);
  const [interventionRule, setInterventionRule] = useState(null);
  const [interventionRuleList, setInterventionRuleList] = useState([]);
  const [course, setCourse] = useState(null);
  const [details, setDetails] = useState("");
  const [helpResources, setHelpResources] = useState(null);
  const [requestHelpResponse, callRequestHelpAPI] = useStarfishAPI();
  const [helpResourcesResponse, callHelpResourcesAPI] = useStarfishAPI();

  useEffect(() => {
    callHelpResourcesAPI('../help-desk-information.html', { method: 'GET' });
  }, []);

  useEffect(() => {
    if (helpResourcesResponse.success) setHelpResources(helpResourcesResponse.data);
  }, [helpResourcesResponse]);

  const clear = () => {
    setInterventionRule(null);
    setCourse(null);
    setDetails("");
    if (closeModal) closeModal();
  };

  const submit = () => {
    if (requestHelpResponse.pending || isFormInvalid) {
      return;
    }
    callRequestHelpAPI('../instructor/saveIntervention.html', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: urlParams({
        studentId: studentId,
        type: interventionRule.id,
        notes: details,
        courseContext: course ? course.id : null
      }, false)
    });
  };

  useEffect(() => {
    if (!requestHelpResponse.pending && requestHelpResponse.error) {
      setToastMessage(requestHelpResponse.error);
    } else if (requestHelpResponse.success) {
      setToastMessage("Thank you for submitting your request.");
      if (successRequestHelp) successRequestHelp();
      clear();
    }
  }, [requestHelpResponse]);

  const getContent = () => {
    return (
        <div className={css["container"]}>
          <RequestHelpHeader/>
          { helpResources && helpResources.showToStudents &&
              <HelpResources studentView helpDeskInformation={helpResources} />
          }
          {studentSelfFlaggingEnabled &&
            <RequestHelpForm studentId={studentId}
                             setToastMessage={setToastMessage}
                             setFormInvalid={setFormInvalid}
                             interventionRule={interventionRule}
                             course={course}
                             details={details}
                             setInterventionRule={setInterventionRule}
                             setCourse={setCourse}
                             setDetails={setDetails}
                             interventionRuleList={interventionRuleList}
                             setInterventionRuleList={setInterventionRuleList}
            />
          }
          {toastMessage && <Toast message={toastMessage} onTimeout={() => setToastMessage("")} />}
        </div>
    )
  };

  const getFooter = () => {

    return (
        <Footer
            primaryButton={<StyledButton aria-disabled={isFormInvalid} onClick={submit}>Submit</StyledButton>}
            secondaryButton={<StyledButton asLink onClick={clear}>{cancelButtonText}</StyledButton>}
        />
    )
  };

  if (!isStudent || !requestHelpEnabled) return <RequestHelpError />
  return <StickyFooterLayout content={getContent()}
                             footer={ studentSelfFlaggingEnabled && interventionRuleList.length > 0
                               ? getFooter() : null } />
};

App.defaultProps = {
  cancelButtonText: 'Clear'
};

App.propTypes = {
  isStudent: PropTypes.bool.isRequired,
  studentId: PropTypes.number.isRequired,
  requestHelpEnabled: PropTypes.bool.isRequired,
  cancelButtonText: PropTypes.string,
  closeModal: PropTypes.func,
  successRequestHelp: PropTypes.func,
  studentSelfFlaggingEnabled: PropTypes.bool
};

export default hot(module)(App);
